import { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, ReferenceArea, PieChart, Pie, Cell } from 'recharts';
import { AppLayout } from './AppLayout';
import { ApiService } from '../services/apiService';
import { TrendResultsList } from './TrendResultsList';
import { TrendCategoryCard } from './TrendCategoryCard';
import { CreateTrendCategoryDialog } from './CreateTrendCategoryDialog';
import { formatTrendQuery } from './TrendQueryBuilder';
import { PieChartPopup } from './PieChartPopup';
import { CivilLawsuitBucketHit } from '../types';
import { useViewMode } from '../contexts/ViewModeContext';

type IntervalOption = 'day' | 'week' | 'month' | 'quarter' | 'year';

interface TrendCategory {
  id: string;
  name: string;
  query: string;
  queryDefinition: import('../types/trendQuery').TrendQueryDefinition;
  fields: string[];
  threshold: number;
  status: string;
  createdAt: string;
  totalChunks: number;
  completedChunks: number;
  totalMatches: number;
  backfillStartedAt?: string;
  backfillCompletedAt?: string;
  lastScoredAt?: string;
}

interface HistogramBucket {
  date: string;
  count: number;
}

interface FacetBucket {
  value: string;
  count: number;
}

type TrendResultHit = CivilLawsuitBucketHit;

/** Compute auto-interval from a date range span in months. */
const computeAutoInterval = (fromDate: string | null, toDate: string | null): IntervalOption => {
  if (!fromDate || !toDate) return 'month';
  const from = new Date(fromDate);
  const to = new Date(toDate);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return 'month';
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (months > 24) return 'month';
  if (months >= 1) return 'week';
  return 'day';
};

const formatRangeDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

const formatBucketDate = (dateStr: string, interval: IntervalOption): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  switch (interval) {
    case 'day': return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'week': return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'month': return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    case 'quarter': return `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
    case 'year': return d.getFullYear().toString();
    default: return dateStr;
  }
};

/** Fills missing date buckets with zero counts between from/to for the given interval. Uses UTC arithmetic to avoid timezone drift. */
const zeroFillBuckets = (buckets: HistogramBucket[], from: string, to: string, interval: IntervalOption): HistogramBucket[] => {
  if (!from || !to) return buckets;
  const start = new Date(from + 'T00:00:00Z');
  const end = new Date(to + 'T00:00:00Z');
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return buckets;

  const bucketMap = new Map<string, HistogramBucket>();
  buckets.forEach(b => bucketMap.set(b.date.slice(0, 10), b));

  const filled: HistogramBucket[] = [];
  const cur = new Date(start);

  if (interval === 'month') cur.setUTCDate(1);
  else if (interval === 'quarter') { cur.setUTCDate(1); cur.setUTCMonth(Math.floor(cur.getUTCMonth() / 3) * 3); }
  else if (interval === 'year') { cur.setUTCDate(1); cur.setUTCMonth(0); }
  else if (interval === 'week') { const day = cur.getUTCDay(); cur.setUTCDate(cur.getUTCDate() - (day === 0 ? 6 : day - 1)); }

  while (cur <= end) {
    const key = cur.toISOString().slice(0, 10);
    const existing = bucketMap.get(key);
    filled.push(existing ?? { date: key, count: 0 });

    if (interval === 'day') cur.setUTCDate(cur.getUTCDate() + 1);
    else if (interval === 'week') cur.setUTCDate(cur.getUTCDate() + 7);
    else if (interval === 'month') cur.setUTCMonth(cur.getUTCMonth() + 1);
    else if (interval === 'quarter') cur.setUTCMonth(cur.getUTCMonth() + 3);
    else if (interval === 'year') cur.setUTCFullYear(cur.getUTCFullYear() + 1);
  }

  return filled;
};

const PIE_COLORS = [
  '#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#6b7280',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#a855f7',
  '#e11d48', '#0ea5e9', '#eab308', '#22c55e', '#d946ef', '#64748b',
  '#fb923c', '#2dd4bf'
];

const buildPieData = (facetBuckets: FacetBucket[], maxItems = 50): { name: string; value: number }[] => {
  if (facetBuckets.length === 0) return [];
  const sorted = [...facetBuckets].sort((a, b) => b.count - a.count);
  const top = sorted.slice(0, maxItems).map(b => ({ name: b.value, value: b.count }));
  const otherCount = sorted.slice(maxItems).reduce((sum, b) => sum + b.count, 0);
  if (otherCount > 0) top.push({ name: 'Other', value: otherCount });
  return top;
};

export const PrecomputedTrendsPage = () => {
  const { viewMode } = useViewMode();
  const [categories, setCategories] = useState<TrendCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncingCategoryIds, setSyncingCategoryIds] = useState<Set<string>>(new Set());
  const [backfillingCategoryIds, setBackfillingCategoryIds] = useState<Set<string>>(new Set());

  // Faceted multi-select filters
  const [selectedCourtStates, setSelectedCourtStates] = useState<string[]>([]);
  const [selectedCourtNames, setSelectedCourtNames] = useState<string[]>([]);
  const [selectedDefendants, setSelectedDefendants] = useState<string[]>([]);
  const [selectedPlaintiffLawFirms, setSelectedPlaintiffLawFirms] = useState<string[]>([]);

  // Facet data from backend
  const [facets, setFacets] = useState<{
    courtStates: FacetBucket[];
    courtNames: FacetBucket[];
    defendants: FacetBucket[];
    plaintiffLawyerFirms: FacetBucket[];
    minFiledAt?: string;
    maxFiledAt?: string;
  }>({ courtStates: [], courtNames: [], defendants: [], plaintiffLawyerFirms: [] });

  // Full date range (unaffected by zoom) — set once per category
  const [fullRangeMin, setFullRangeMin] = useState<string | undefined>(undefined);
  const [fullRangeMax, setFullRangeMax] = useState<string | undefined>(undefined);

  // Zoom state
  const [zoomFrom, setZoomFrom] = useState<string | null>(null);
  const [zoomTo, setZoomTo] = useState<string | null>(null);
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [overviewBuckets, setOverviewBuckets] = useState<HistogramBucket[]>([]);
  const [overviewRefLeft, setOverviewRefLeft] = useState<string | null>(null);
  const [overviewRefRight, setOverviewRefRight] = useState<string | null>(null);
  const [isOverviewSelecting, setIsOverviewSelecting] = useState(false);

  const [buckets, setBuckets] = useState<HistogramBucket[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingHistogram, setIsLoadingHistogram] = useState(false);

  const [listHits, setListHits] = useState<TrendResultHit[]>([]);
  const [listTotalCount, setListTotalCount] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [isListLoading, setIsListLoading] = useState(false);

  // Pie chart popup state
  const [piePopup, setPiePopup] = useState<{ title: string; field: 'defendants' | 'plaintiffLawyerFirms' } | null>(null);

  const apiService = ApiService.getInstance();
  const PAGE_SIZE = 100;
  const refreshTimerRef = useRef<number | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiService.listTrendCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setIsLoadingCategories(false);
    }
  }, [apiService]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Auto-refresh categories every 10s while any is backfilling
  useEffect(() => {
    const hasBackfilling = categories.some(c => c.status === 'backfilling');
    if (hasBackfilling) {
      const tick = () => { loadCategories(); };
      refreshTimerRef.current = window.setInterval(tick, 10000);
    } else {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [categories, loadCategories]);

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Derive date range from facets (ES min/max) or zoom
  const effectiveFrom = zoomFrom ?? fullRangeMin?.slice(0, 10) ?? null;
  const effectiveTo = zoomTo ?? fullRangeMax?.slice(0, 10) ?? null;
  const fullRangeFrom = fullRangeMin?.slice(0, 10) ?? null;
  const fullRangeTo = fullRangeMax?.slice(0, 10) ?? null;
  const overviewInterval = computeAutoInterval(fullRangeFrom, fullRangeTo);
  const interval = computeAutoInterval(zoomFrom ?? fullRangeFrom, zoomTo ?? fullRangeTo);

  // Disjunctive facets: each pie chart field excludes its own filter so selections
  // highlight slices without collapsing the chart to only the selected value.
  const loadFacets = useCallback(async () => {
    if (!selectedCategoryId) return;
    try {
      const baseFilters = {
        courtStates: selectedCourtStates.length > 0 ? selectedCourtStates : undefined,
        courtNames: selectedCourtNames.length > 0 ? selectedCourtNames : undefined,
      };

      // Three parallel calls: base (with all filters), defendants-disjunctive, plaintiffLawFirms-disjunctive
      const [baseResult, defendantsFacets, plaintiffFacets] = await Promise.all([
        apiService.getTrendCategoryFacets(selectedCategoryId, {
          ...baseFilters,
          defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
          plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
        }),
        // Defendants pie: exclude defendant filter so full distribution is visible
        apiService.getTrendCategoryFacets(selectedCategoryId, {
          ...baseFilters,
          plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
        }),
        // Plaintiff pie: exclude plaintiff filter
        apiService.getTrendCategoryFacets(selectedCategoryId, {
          ...baseFilters,
          defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
        }),
      ]);

      setFacets({
        courtStates: baseResult.courtStates,
        courtNames: baseResult.courtNames,
        defendants: defendantsFacets.defendants,
        plaintiffLawyerFirms: plaintiffFacets.plaintiffLawyerFirms,
        minFiledAt: baseResult.minFiledAt,
        maxFiledAt: baseResult.maxFiledAt,
      });
      // Preserve full date range (not narrowed by zoom)
      if (baseResult.minFiledAt) setFullRangeMin(baseResult.minFiledAt);
      if (baseResult.maxFiledAt) setFullRangeMax(baseResult.maxFiledAt);
    } catch (error) {
      console.error('Failed to load facets:', error);
    }
  }, [apiService, selectedCategoryId, selectedCourtStates, selectedCourtNames, selectedDefendants, selectedPlaintiffLawFirms]);

  const loadHistogram = useCallback(async () => {
    if (!selectedCategoryId) return;
    setIsLoadingHistogram(true);
    try {
      const baseFilters = {
        courtStates: selectedCourtStates.length > 0 ? selectedCourtStates : undefined,
        courtNames: selectedCourtNames.length > 0 ? selectedCourtNames : undefined,
        defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
        plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
      };

      const from = fullRangeMin?.slice(0, 10) || undefined;
      const to = fullRangeMax?.slice(0, 10) || undefined;

      // Always fetch full range for overview chart
      const fullRangeResponse = await apiService.getTrendCategoryHistogram(selectedCategoryId, {
        filters: { ...baseFilters, filedAtFrom: from, filedAtTo: to },
        interval: overviewInterval
      });
      const filledOverview = zeroFillBuckets(fullRangeResponse.buckets, from ?? '', to ?? '', overviewInterval);
      setOverviewBuckets(filledOverview);

      if (zoomFrom && zoomTo) {
        // Fetch zoomed range for main chart
        const zoomedResponse = await apiService.getTrendCategoryHistogram(selectedCategoryId, {
          filters: { ...baseFilters, filedAtFrom: zoomFrom, filedAtTo: zoomTo },
          interval
        });
        setBuckets(zeroFillBuckets(zoomedResponse.buckets, zoomFrom, zoomTo, interval));
        setTotalCount(zoomedResponse.totalCount);
      } else {
        setBuckets(filledOverview);
        setTotalCount(fullRangeResponse.totalCount);
      }
    } catch (error) {
      console.error('Failed to load histogram:', error);
      setBuckets([]);
      setOverviewBuckets([]);
      setTotalCount(0);
    } finally {
      setIsLoadingHistogram(false);
    }
  }, [apiService, selectedCategoryId, selectedCourtStates, selectedCourtNames, selectedDefendants, selectedPlaintiffLawFirms, interval, overviewInterval, zoomFrom, zoomTo, fullRangeMin, fullRangeMax]);

  const loadList = useCallback(async (page: number) => {
    if (!selectedCategoryId) return;
    setIsListLoading(true);
    try {
      const listFilters = {
        courtStates: selectedCourtStates.length > 0 ? selectedCourtStates : undefined,
        courtNames: selectedCourtNames.length > 0 ? selectedCourtNames : undefined,
        defendants: selectedDefendants.length > 0 ? selectedDefendants : undefined,
        plaintiffLawyerFirms: selectedPlaintiffLawFirms.length > 0 ? selectedPlaintiffLawFirms : undefined,
        filedAtFrom: (zoomFrom || fullRangeMin?.slice(0, 10)) || undefined,
        filedAtTo: (zoomTo || fullRangeMax?.slice(0, 10)) || undefined
      };

      const response = await apiService.getTrendCategoryList(selectedCategoryId, {
        filters: listFilters,
        page,
        pageSize: PAGE_SIZE
      });
      setListHits(response.hits);
      setListTotalCount(response.totalCount);
      setListPage(page);
    } catch (error) {
      console.error('Failed to load list:', error);
      setListHits([]);
      setListTotalCount(0);
    } finally {
      setIsListLoading(false);
    }
  }, [apiService, selectedCategoryId, selectedCourtStates, selectedCourtNames, selectedDefendants, selectedPlaintiffLawFirms, zoomFrom, zoomTo, fullRangeMin, fullRangeMax]);

  // Load data when category is first selected (or becomes ready)
  const prevCategoryRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedCategoryId && selectedCategory?.status === 'ready') {
      if (prevCategoryRef.current !== selectedCategoryId) {
        prevCategoryRef.current = selectedCategoryId;
        loadFacets();
        loadHistogram();
        loadList(1);
      }
    } else {
      prevCategoryRef.current = null;
      setFacets({ courtStates: [], courtNames: [], defendants: [], plaintiffLawyerFirms: [] });
      setBuckets([]);
      setTotalCount(0);
      setListHits([]);
      setListTotalCount(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId, selectedCategory?.status]);

  // Debounced reload when filters change
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialFilterMount = useRef(true);
  const prevZoomRef = useRef<{ from: string | null; to: string | null }>({ from: null, to: null });

  useEffect(() => {
    // Skip initial mount to avoid double-fire with category selection effect
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      prevZoomRef.current = { from: zoomFrom, to: zoomTo };
      return;
    }

    // If zoom was just cleared (reset), skip refetch — resetZoom already set buckets synchronously
    const wasZoomed = prevZoomRef.current.from !== null || prevZoomRef.current.to !== null;
    const isNowCleared = zoomFrom === null && zoomTo === null;
    prevZoomRef.current = { from: zoomFrom, to: zoomTo };

    if (wasZoomed && isNowCleared) {
      return;
    }

    if (selectedCategory?.status === 'ready') {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => {
        loadFacets();
        loadHistogram();
        loadList(1);
      }, 800);
      return () => { if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current); };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourtStates, selectedCourtNames, selectedDefendants, selectedPlaintiffLawFirms, zoomFrom, zoomTo]);

  // Reset zoom and filters when category deselected
  useEffect(() => {
    if (!selectedCategoryId) {
      setZoomFrom(null);
      setZoomTo(null);
      setFullRangeMin(undefined);
      setFullRangeMax(undefined);
      setSelectedCourtStates([]);
      setSelectedCourtNames([]);
      setSelectedDefendants([]);
      setSelectedPlaintiffLawFirms([]);
    }
  }, [selectedCategoryId]);

  const handleDeleteCategory = async (id: string) => {
    try {
      await apiService.deleteTrendCategory(id);
      if (selectedCategoryId === id) {
        setSelectedCategoryId(null);
      }
      loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  };

  const handleSyncCategory = async (id: string) => {
    setSyncingCategoryIds(prev => new Set(prev).add(id));
    try {
      await apiService.syncTrendCategory(id);
      setTimeout(loadCategories, 2000);
    } catch (error) {
      console.error('Failed to sync category:', error);
    } finally {
      setSyncingCategoryIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleBackfillCategory = async (id: string) => {
    setBackfillingCategoryIds(prev => new Set(prev).add(id));
    try {
      await apiService.backfillTrendCategory(id);
      setTimeout(loadCategories, 2000);
    } catch (error) {
      console.error('Failed to backfill category:', error);
    } finally {
      setBackfillingCategoryIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handlePieSliceClick = (field: 'defendants' | 'plaintiffLawyerFirms', name: string) => {
    if (name === 'Other') return;
    const setter = field === 'defendants' ? setSelectedDefendants : setSelectedPlaintiffLawFirms;
    const current = field === 'defendants' ? selectedDefendants : selectedPlaintiffLawFirms;
    if (current.includes(name)) {
      setter(current.filter(v => v !== name));
    } else {
      setter([name]);
    }
  };

  const chartData = buckets.map(b => ({
    label: formatBucketDate(b.date, interval),
    count: b.count,
    date: b.date
  }));

  const overviewChartData = overviewBuckets.map(b => ({
    label: formatBucketDate(b.date, overviewInterval),
    count: b.count,
    date: b.date
  }));

  const isZoomed = zoomFrom !== null && zoomTo !== null;

  // Chart zoom handlers
  const handleMouseDown = (e: any) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  };

  const handleMouseUp = () => {
    if (!isSelecting || !refAreaLeft || !refAreaRight) {
      setIsSelecting(false);
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    let leftIdx = chartData.findIndex(d => d.label === refAreaLeft);
    let rightIdx = chartData.findIndex(d => d.label === refAreaRight);
    if (leftIdx > rightIdx) [leftIdx, rightIdx] = [rightIdx, leftIdx];
    if (leftIdx === rightIdx || leftIdx < 0 || rightIdx < 0) {
      setIsSelecting(false);
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    const fromDate = chartData[leftIdx].date;
    const toDate = chartData[rightIdx].date;
    setZoomFrom(fromDate);
    setZoomTo(toDate);
    setIsSelecting(false);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  // After zoom is cleared, reload facets and list with full-range (new callbacks have null zoom)
  const zoomJustClearedRef = useRef(false);

  const resetZoom = () => {
    zoomJustClearedRef.current = true;
    setZoomFrom(null);
    setZoomTo(null);
    setBuckets(overviewBuckets);
    setTotalCount(overviewBuckets.reduce((sum, b) => sum + b.count, 0));
  };

  useEffect(() => {
    if (zoomJustClearedRef.current && zoomFrom === null && zoomTo === null && selectedCategory?.status === 'ready') {
      zoomJustClearedRef.current = false;
      loadFacets();
      loadList(1);
    }
  }, [zoomFrom, zoomTo, loadFacets, loadList, selectedCategory?.status]);

  const isBackfilling = selectedCategory?.status === 'backfilling';
  const isNotReady = selectedCategory && selectedCategory.status !== 'ready';

  return (
    <AppLayout>
      <main data-component="PrecomputedTrendsPage" className="flex-1 overflow-auto">
        <div className="flex h-full">
          {/* Left Sidebar */}
          <aside className="w-72 flex-shrink-0 border-r border-gray-200 bg-gray-50 p-4 overflow-y-auto">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Filters</h2>

            {!selectedCategoryId ? (
              <div className="text-sm text-gray-500 py-4">Select a topic to enable filters.</div>
            ) : (<>
            <FacetMultiSelect
              label="Court State"
              options={facets.courtStates}
              selected={selectedCourtStates}
              onChange={setSelectedCourtStates}
            />

            <FacetMultiSelect
              label="Court"
              options={facets.courtNames}
              selected={selectedCourtNames}
              onChange={setSelectedCourtNames}
            />

            <FacetMultiSelect
              label="Defendant"
              options={facets.defendants}
              selected={selectedDefendants}
              onChange={setSelectedDefendants}
            />

            <FacetMultiSelect
              label="Plaintiff Law Firm"
              options={facets.plaintiffLawyerFirms}
              selected={selectedPlaintiffLawFirms}
              onChange={setSelectedPlaintiffLawFirms}
            />

            {(effectiveFrom || effectiveTo) && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-400">
                  Range: {effectiveFrom ? formatRangeDate(effectiveFrom) : '?'} — {effectiveTo ? formatRangeDate(effectiveTo) : '?'}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Interval: {interval} (auto)
                </div>
              </div>
            )}
            </>)}
          </aside>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">

            {/* Topic List */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Topics</h2>
                <div className="flex items-center gap-2">
                  {viewMode === 'admin' && (
                    <>
                      <button
                        onClick={async () => {
                          const data = await apiService.exportTrendCategories();
                          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'trend-categories.json';
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center gap-1.5"
                        title="Export topics as JSON"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                        </svg>
                        Export
                      </button>
                      <label
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center gap-1.5 cursor-pointer"
                        title="Import topics from JSON (only query required)"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M17 8l-5-5-5 5M12 3v12" />
                        </svg>
                        Import
                        <input
                          type="file"
                          accept=".json"
                          className="hidden"
                          onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const text = await file.text();
                              const items = JSON.parse(text);
                              const arr = Array.isArray(items) ? items : [items];
                              await apiService.importTrendCategories(arr);
                              loadCategories();
                            } catch (err) {
                              console.error('Import failed:', err);
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <div className="relative group inline-block">
                        <button
                          onClick={async () => {
                            setIsSyncing(true);
                            try {
                              await apiService.syncTrendCategories();
                              loadCategories();
                            } finally {
                              setIsSyncing(false);
                            }
                          }}
                          disabled={isSyncing}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
                        >
                          {isSyncing ? 'Syncing...' : 'Sync Now'}
                          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" strokeWidth="2" />
                            <path strokeLinecap="round" strokeWidth="2" d="M12 8v4M12 16h.01" />
                          </svg>
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover:block w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-20">
                          Re-scores all topics against newly ingested documents that haven't been scored yet. This runs automatically every hour.
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-800" />
                        </div>
                      </div>
                    </>
                  )}
                  <button
                    onClick={() => setShowCreateDialog(true)}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    + Add Topic
                  </button>
                </div>
              </div>

              {categories.length > 0 && (
                <input
                  type="text"
                  value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)}
                  placeholder="Search topics..."
                  aria-label="Search topics"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-3"
                />
              )}

              {isLoadingCategories ? (
                <div className="text-sm text-gray-500 py-4">Loading topics...</div>
              ) : categories.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500">No topics yet.</p>
                  <p className="text-sm text-gray-400 mt-1">Create your first trend topic to get started.</p>
                </div>
              ) : (
                <div className="flex gap-3 overflow-x-auto pt-12 -mt-12 pb-2">
                  {filteredCategories.map(cat => (
                    <TrendCategoryCard
                      key={cat.id}
                      category={cat}
                      isSelected={selectedCategoryId === cat.id}
                      isSyncing={syncingCategoryIds.has(cat.id)}
                      isBackfilling={backfillingCategoryIds.has(cat.id)}
                      viewMode={viewMode}
                      onSelect={() => setSelectedCategoryId(cat.id)}
                      onDelete={() => handleDeleteCategory(cat.id)}
                      onSync={() => handleSyncCategory(cat.id)}
                      onBackfill={() => handleBackfillCategory(cat.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Selected Category Details */}
            {selectedCategory ? (
              <>
                <div data-flow="category-definition" className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Query definition</span>
                    <code className="text-sm text-gray-800">{formatTrendQuery(selectedCategory.queryDefinition.root)}</code>
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">View structured definition</summary>
                    <pre className="mt-2 max-h-48 overflow-auto rounded bg-white p-2 text-xs text-gray-600">{JSON.stringify(selectedCategory.queryDefinition, null, 2)}</pre>
                  </details>
                </div>
                {isNotReady ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <p className="text-amber-800">
                      {isBackfilling
                        ? `Backfilling... ${selectedCategory.completedChunks}/${selectedCategory.totalChunks} chunks completed.`
                        : `Category status: ${selectedCategory.status}`}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Histogram */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                          {isLoadingHistogram ? 'Loading...' : (
                            <>
                              {effectiveFrom && effectiveTo
                                ? `${formatRangeDate(effectiveFrom)} to ${formatRangeDate(effectiveTo)}`
                                : 'Filing Trends'}{' '}
                              ({totalCount.toLocaleString()} total)
                              {isZoomed && (
                                <span className="text-sm font-normal text-blue-600 ml-2">
                                  — viewing {listTotalCount.toLocaleString()} in selection
                                </span>
                              )}
                            </>
                          )}
                        </h3>
                        <div className="flex items-center gap-3">
                          {isZoomed && (
                            <button onClick={resetZoom}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                              Reset Zoom
                            </button>
                          )}
                          {!isLoadingHistogram && chartData.length > 0 && (
                            <span className="text-xs text-gray-400">Drag on chart to zoom</span>
                          )}
                        </div>
                      </div>

                      {!isLoadingHistogram && chartData.length > 0 ? (<>
                        <div className="flex gap-4">
                          {/* Pie charts column */}
                          <div className="flex flex-col gap-2 w-[220px] flex-shrink-0">
                            {/* Defendants pie */}
                            <div className="flex-1 flex flex-col items-center">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="text-xs font-medium text-gray-500">Defendants</span>
                                <button
                                  onClick={() => setPiePopup({ title: 'Defendants', field: 'defendants' })}
                                  className="text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Expand chart"
                                  aria-label="Expand defendants chart"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                                  </svg>
                                </button>
                              </div>
                              <ResponsiveContainer width="100%" height={140}>
                                <PieChart>
                                  <Pie
                                    data={buildPieData(facets.defendants)}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={55}
                                    innerRadius={25}
                                    onClick={(_, idx) => {
                                      const item = buildPieData(facets.defendants)[idx];
                                      if (item) handlePieSliceClick('defendants', item.name);
                                    }}
                                    cursor="pointer"
                                  >
                                    {buildPieData(facets.defendants).map((entry, i) => (
                                      <Cell
                                        key={i}
                                        fill={PIE_COLORS[i % PIE_COLORS.length]}
                                        stroke={selectedDefendants.includes(entry.name) ? '#1d4ed8' : '#fff'}
                                        strokeWidth={selectedDefendants.includes(entry.name) ? 3 : 1}
                                        opacity={selectedDefendants.length > 0 && !selectedDefendants.includes(entry.name) ? 0.4 : 1}
                                      />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value: any, name: any) => [Number(value).toLocaleString(), name ?? '']} />
                                </PieChart>
                              </ResponsiveContainer>
                              {selectedDefendants.length > 0 && (
                                <span className="text-[10px] text-blue-600 mt-0.5">{selectedDefendants.length} filtered</span>
                              )}
                            </div>
                            {/* Plaintiff Law Firms pie */}
                            <div className="flex-1 flex flex-col items-center">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="text-xs font-medium text-gray-500">Plaintiff Law Firms</span>
                                <button
                                  onClick={() => setPiePopup({ title: 'Plaintiff Law Firms', field: 'plaintiffLawyerFirms' })}
                                  className="text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Expand chart"
                                  aria-label="Expand plaintiff law firms chart"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                                  </svg>
                                </button>
                              </div>
                              <ResponsiveContainer width="100%" height={140}>
                                <PieChart>
                                  <Pie
                                    data={buildPieData(facets.plaintiffLawyerFirms)}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={55}
                                    innerRadius={25}
                                    onClick={(_, idx) => {
                                      const item = buildPieData(facets.plaintiffLawyerFirms)[idx];
                                      if (item) handlePieSliceClick('plaintiffLawyerFirms', item.name);
                                    }}
                                    cursor="pointer"
                                  >
                                    {buildPieData(facets.plaintiffLawyerFirms).map((entry, i) => (
                                      <Cell
                                        key={i}
                                        fill={PIE_COLORS[i % PIE_COLORS.length]}
                                        stroke={selectedPlaintiffLawFirms.includes(entry.name) ? '#1d4ed8' : '#fff'}
                                        strokeWidth={selectedPlaintiffLawFirms.includes(entry.name) ? 3 : 1}
                                        opacity={selectedPlaintiffLawFirms.length > 0 && !selectedPlaintiffLawFirms.includes(entry.name) ? 0.4 : 1}
                                      />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value: any, name: any) => [Number(value).toLocaleString(), name ?? '']} />
                                </PieChart>
                              </ResponsiveContainer>
                              {selectedPlaintiffLawFirms.length > 0 && (
                                <span className="text-[10px] text-blue-600 mt-0.5">{selectedPlaintiffLawFirms.length} filtered</span>
                              )}
                            </div>
                          </div>

                          {/* Line chart column */}
                          <div className="flex-1 min-w-0">
                            <ResponsiveContainer width="100%" height={300}>
                              <ComposedChart
                                data={chartData}
                                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                                <Tooltip
                                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', padding: '8px 12px' }}
                                  formatter={(value: any) => [Number(value).toLocaleString(), 'Filings']}
                                />
                                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
                                {refAreaLeft && refAreaRight && (
                                  <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} fill="#3b82f6" fillOpacity={0.15} />
                                )}
                              </ComposedChart>
                            </ResponsiveContainer>

                            {overviewChartData.length > 0 && (
                              <div className="mt-2 relative ml-[50px] mr-[30px]">
                                <div className="relative">
                                  <ResponsiveContainer width="100%" height={40}>
                                    <ComposedChart
                                      data={overviewChartData}
                                      margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                                    >
                                      <Line type="monotone" dataKey="count" stroke="#cbd5e1" strokeWidth={1} dot={false} />
                                      {isZoomed && zoomFrom && zoomTo && (() => {
                                        const fromIdx = overviewChartData.findIndex(d => d.date >= zoomFrom);
                                        let toIdx = -1;
                                        for (let i = overviewChartData.length - 1; i >= 0; i--) {
                                          if (overviewChartData[i].date <= zoomTo) { toIdx = i; break; }
                                        }
                                        const x1Label = fromIdx >= 0 ? overviewChartData[fromIdx].label : null;
                                        const x2Label = toIdx >= 0 ? overviewChartData[toIdx].label : null;
                                        if (!x1Label || !x2Label) return null;
                                        return (
                                          <ReferenceArea
                                            x1={x1Label}
                                            x2={x2Label}
                                            fill="#3b82f6"
                                            fillOpacity={0.2}
                                            stroke="#3b82f6"
                                            strokeOpacity={0.5}
                                          />
                                        );
                                      })()}
                                      {overviewRefLeft && overviewRefRight && (() => {
                                        const left = Math.min(Number(overviewRefLeft), Number(overviewRefRight));
                                        const right = Math.max(Number(overviewRefLeft), Number(overviewRefRight));
                                        const leftIdx = Math.max(0, Math.min(overviewChartData.length - 1, Math.round(left * (overviewChartData.length - 1))));
                                        const rightIdx = Math.max(0, Math.min(overviewChartData.length - 1, Math.round(right * (overviewChartData.length - 1))));
                                        return (
                                          <ReferenceArea
                                            x1={overviewChartData[leftIdx].label}
                                            x2={overviewChartData[rightIdx].label}
                                            fill="#2563eb"
                                            fillOpacity={0.25}
                                            stroke="#2563eb"
                                            strokeOpacity={0.6}
                                          />
                                        );
                                      })()}
                                      <XAxis dataKey="label" hide />
                                      <YAxis hide />
                                    </ComposedChart>
                                  </ResponsiveContainer>
                                  {/* Transparent overlay for drag selection anywhere */}
                                  <div
                                    className="absolute inset-0 cursor-crosshair"
                                    onMouseDown={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const pct = (e.clientX - rect.left) / rect.width;
                                      setOverviewRefLeft(String(Math.max(0, Math.min(1, pct))));
                                      setIsOverviewSelecting(true);
                                    }}
                                    onMouseMove={(e) => {
                                      if (!isOverviewSelecting) return;
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const pct = (e.clientX - rect.left) / rect.width;
                                      setOverviewRefRight(String(Math.max(0, Math.min(1, pct))));
                                    }}
                                    onMouseUp={() => {
                                      if (!isOverviewSelecting || !overviewRefLeft || !overviewRefRight) {
                                        setIsOverviewSelecting(false);
                                        setOverviewRefLeft(null);
                                        setOverviewRefRight(null);
                                        return;
                                      }
                                      const left = Math.min(Number(overviewRefLeft), Number(overviewRefRight));
                                      const right = Math.max(Number(overviewRefLeft), Number(overviewRefRight));
                                      const leftIdx = Math.max(0, Math.min(overviewChartData.length - 1, Math.round(left * (overviewChartData.length - 1))));
                                      const rightIdx = Math.max(0, Math.min(overviewChartData.length - 1, Math.round(right * (overviewChartData.length - 1))));
                                      if (leftIdx === rightIdx) {
                                        setIsOverviewSelecting(false);
                                        setOverviewRefLeft(null);
                                        setOverviewRefRight(null);
                                        return;
                                      }
                                      setZoomFrom(overviewChartData[leftIdx].date);
                                      setZoomTo(overviewChartData[rightIdx].date);
                                      setIsOverviewSelecting(false);
                                      setOverviewRefLeft(null);
                                      setOverviewRefRight(null);
                                    }}
                                    onMouseLeave={() => {
                                      if (isOverviewSelecting) {
                                        setIsOverviewSelecting(false);
                                        setOverviewRefLeft(null);
                                        setOverviewRefRight(null);
                                      }
                                    }}
                                  />
                                </div>
                                <div className="absolute top-0 right-0 text-[10px] text-gray-400">
                                  {isZoomed ? 'drag to re-select range' : 'drag to zoom'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>) : !isLoadingHistogram ? (
                        <div className="text-center text-gray-500 py-12">No data found.</div>
                      ) : null}
                    </div>

                    {/* Results List */}
                    <TrendResultsList
                      hits={listHits}
                      totalCount={listTotalCount}
                      page={listPage}
                      pageSize={PAGE_SIZE}
                      isLoading={isListLoading}
                      isZoomed={isZoomed}
                      onPageChange={loadList}
                    />
                  </>
                )}
              </>
            ) : categories.length > 0 ? (
              <div className="text-center text-gray-500 py-12">
                Select a category to view its trends.
              </div>
            ) : null}
          </div>
        </div>

        <CreateTrendCategoryDialog
          isOpen={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            loadCategories();
            setShowCreateDialog(false);
          }}
          viewMode={viewMode}
        />

        {piePopup && (
          <PieChartPopup
            isOpen={true}
            onClose={() => setPiePopup(null)}
            title={piePopup.title}
            data={piePopup.field === 'defendants' ? facets.defendants : facets.plaintiffLawyerFirms}
            selected={piePopup.field === 'defendants' ? selectedDefendants : selectedPlaintiffLawFirms}
            onSelect={(name) => handlePieSliceClick(piePopup.field, name)}
          />
        )}
      </main>
    </AppLayout>
  );
};

const FacetMultiSelect = ({ label, options, selected, onChange }: {
  label: string;
  options: FacetBucket[];
  selected: string[];
  onChange: (v: string[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonId = `facet-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const filtered = options.filter(o => o.value.toLowerCase().includes(search.toLowerCase()));
  const top5 = options.slice(0, 5);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  return (
    <div ref={ref} data-component="FacetMultiSelect" className="mt-3 first:mt-0 relative">
      <label htmlFor={buttonId} className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">{label}</label>

      <button
        id={buttonId}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={`Filter by ${label}`}
        className="w-full flex items-center justify-between px-2 py-1.5 border border-gray-300 rounded text-sm bg-white hover:border-gray-400 mb-1.5"
      >
        <span className="text-xs text-gray-500">{options.length > 5 ? `${options.length - 5} more…` : 'All'}</span>
        <span className="flex items-center gap-1">
          {selected.length > 0 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{selected.length}</span>
          )}
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              aria-label={`Search ${label} options`}
              className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No results</div>
            ) : (
              filtered.map(opt => (
                <label key={opt.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="flex-1 text-xs text-gray-700 truncate">{opt.value}</span>
                  <span className="text-xs text-gray-400">({opt.count.toLocaleString()})</span>
                </label>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={() => onChange([])}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {/* Top 5 quick-select items */}
      {top5.length > 0 && (
        <div>
          {top5.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 px-1 py-1 hover:bg-gray-100 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded"
              />
              <span className="flex-1 text-xs text-gray-700 truncate">{opt.value}</span>
              <span className="text-[10px] text-gray-400">({opt.count.toLocaleString()})</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

